<!-- eslint-disable vue/no-mutating-props -->
<template>
  <div class="accounting-tab" :class="{ 'form-disabled': disabled }">
    <!-- Accounting (FORM02: accounting.cat_emcr_valuations). Split valuation (INALUM/TIMAH, `split_rows` config): Valuation
         Category di baris pertama -> satu baris per Valuation Type category itu; baris hasil split terkunci & mengikuti
         baris pertama. ANTAM tanpa split (Valuation Type dipilih manual), tapi `companion_rows`: Valuation Type C1-C4 dipilih ->
         satu baris Valuation Type "" per Valuation Area + Category (terkunci, tidak bisa dihapus, ikut baris pemicunya). -->
    <div v-if="isTableVisible(ACCOUNTING_TAB, VALUATIONS_FIELD)" class="card">
      <div class="card-header py-3 pb-2 d-flex align-items-center justify-content-between" style="padding-left: 1rem">
        <h6 class="mb-0 fw-semibold">
          Accounting <span v-if="isTableMandatory(ACCOUNTING_TAB, VALUATIONS_FIELD)" class="exclamation-mark-error-tab">*</span>
        </h6>
        <ButtonPrime v-if="canAddRow(ACCOUNTING_TAB, VALUATIONS_FIELD)" icon="pi pi-plus" label="Add" size="small" :disabled="disabled" @click="addRow(ACCOUNTING_TAB, VALUATIONS_FIELD)" />
      </div>
      <div class="card-body pt-0 px-0 card-body-tab">
        <DataTable :value="dialog.form[VALUATIONS_FIELD] || []" class="p-datatable-sm p-datatable-gridlines" scrollable scroll-height="420px">
          <template v-for="col in ACCOUNTING_COLUMNS" :key="col.column">
            <Column v-if="isColumnVisibleFn(col.column, VALUATIONS_FIELD, ACCOUNTING_TAB)" header-class="bg-light text-center" :style="columnWidth(col.width)">
              <template #header>
                {{ col.label }} <span v-if="isColumnMandatoryInRows(ACCOUNTING_TAB, VALUATIONS_FIELD, col.column)" class="exclamation-mark-error-tab">*</span>
              </template>
              <template #body="{ data }">
                <SelectPrime show-clear v-if="col.type === 'select'" v-model="data[col.column]" size="small" fluid :filter="col.filter"
                  :options="dialog.options[columnOptionsKey(ACCOUNTING_TAB, VALUATIONS_FIELD, col.column, data)]"
                  option-label="label" option-value="value" :placeholder="isCellLocked(col.column, data) ? '' : `Select ${col.label}`"
                  :loading="dialog.loading[columnOptionsKey(ACCOUNTING_TAB, VALUATIONS_FIELD, col.column, data)]"
                  :invalid="isColumnInvalidFn(data, col.column)"
                  :disabled="disabled || isCellLocked(col.column, data) || !isColumnEnabledFn(col.column, VALUATIONS_FIELD, ACCOUNTING_TAB)"
                  @show="loadColumnOptions(ACCOUNTING_TAB, VALUATIONS_FIELD, col.column, data)"
                  @change="onColumnChange(ACCOUNTING_TAB, VALUATIONS_FIELD, col.column, data)"
                >
                  <template #value="{ value, placeholder }">
                    <span v-if="value != null && value !== ''">{{ getSelectValueLabelFn(dialog.options[columnOptionsKey(ACCOUNTING_TAB, VALUATIONS_FIELD, col.column, data)], value) }}</span>
                    <span v-else>{{ placeholder }}</span>
                  </template>
                </SelectPrime>
                <InputText v-else-if="col.type === 'locked'" :model-value="data[col.column] ?? ''" size="small" disabled fluid />
                <InputNumber v-else :model-value="toNumberValue(data[col.column])" size="small" placeholder="0" :use-grouping="false" :min="0" fluid
                  :max-fraction-digits="col.fraction"
                  :invalid="isColumnInvalidFn(data, col.column)"
                  :disabled="disabled || isCellLocked(col.column, data) || !isColumnEnabledFn(col.column, VALUATIONS_FIELD, ACCOUNTING_TAB)"
                  @update:model-value="onNumberChange(data, col.column, $event)"
                />
              </template>
            </Column>
          </template>
          <Column v-if="canRemoveRow(ACCOUNTING_TAB, VALUATIONS_FIELD)" header-class="bg-light text-center" style="width: 56px">
            <template #header>Action</template>
            <template #body="{ data, index }">
              <!-- Baris hasil split / pendamping tidak dihapus sendiri: ikut terhapus bersama baris pemicunya. -->
              <ButtonPrime v-if="!isSplitFollower(data) && !isCompanionRow(data)" icon="pi pi-trash" severity="danger" text size="small" :disabled="disabled" @click="removeRow(VALUATIONS_FIELD, index)" />
            </template>
          </Column>
          <template #empty>
            <div class="text-muted text-center small py-3">No accounting data yet.</div>
          </template>
        </DataTable>
      </div>
    </div>
  </div>
</template>

<script>
import { cataloguerTabMixin, useCataloguerTabEngine, createTabDialog } from "./New_CataloguerTab";

// Key tab & field repeatable di FORM02_BASE.json
const ACCOUNTING_TAB = "accounting";
const VALUATIONS_FIELD = "cat_emcr_valuations";

// Layout kolom (urutan, label, jenis input) -- hardcode di Vue sesuai prinsip "layout hardcode, behavior dynamic".
// Tampil/wajib/enabled/opsi & aturan split dari config FORM02 (cat_emcr_valuations.columns, `split_rows`).
const ACCOUNTING_COLUMNS = [
  { column: "valuation_category", label: "Valuation Category", type: "select", width: 190 },
  { column: "valuation_type", label: "Valuation Type", type: "select", width: 190 },
  { column: "valuation_area", label: "Valuation Area", type: "select", width: 170 },
  { column: "valuation_class", label: "Valuation Class", type: "select", width: 220, filter: true },
  { column: "price_control", label: "Price Control", type: "select", width: 190 },
  { column: "std_price", label: "Standard Price", type: "number", width: 160, fraction: 2 },
  { column: "price_unit", label: "Price Unit", type: "number", width: 140, fraction: 0 },
  // Terkunci (`readonlyandsaved`): nilai dari default_value config (INALUM), tetap disimpan.
  { column: "ml_active", label: "ML Active", type: "locked", width: 110 },
  { column: "ml_settle", label: "ML Settle", type: "locked", width: 110 },
  { column: "qty_struct", label: "Qty Struct", type: "locked", width: 110 },
  { column: "original_material", label: "Original Material", type: "locked", width: 150 }
];

// Props, setup engine, dialog, bind ke mcrItem, addRow/removeRow/opsi kolom/split_rows: kerangka bersama New_CataloguerTab.js.
export default {
  name: "AccountingTab",
  mixins: [cataloguerTabMixin],
  setup(props) {
    return useCataloguerTabEngine(props, (vm) => ({
      // Valuation Area = plant di tabel Plant (data_source detail_array, source "form").
      getDetailSources: () => ({ form: vm.dialog.form }),
      // Filter Valuation Class memakai opsi Material Type (tab Basic Data); di INALUM opsi itu berasal dari Item Name terpilih,
      // jadi Item Name dicari per value (dataset besar), bukan dimuat seluruhnya -- sama seperti di tab Basic Data.
      searchAsYouTypeFields: new Set(["cat_inc"])
    }));
  },
  data() {
    return {
      ACCOUNTING_TAB,
      VALUATIONS_FIELD,
      ACCOUNTING_COLUMNS,
      dialog: createTabDialog(this.mcrItem)
    };
  },
  methods: {
    columnWidth(width) {
      return { width: `${width}px`, minWidth: `${width}px` };
    },
    // Baris hasil split: semua kolom terkunci (category ikut baris pertama, type dari split, sisanya disalin dari baris
    // pertama). Valuation Type di tabel yang split-nya aktif selalu terisi otomatis -> terkunci juga di baris pertama.
    // Baris pendamping (`companion_rows`, ANTAM) terkunci seluruhnya: nilainya ikut baris pemicu.
    isCellLocked(column, row) {
      return this.isSplitFollower(row) || this.isCompanionRow(row) || this.isSplitTargetColumn(ACCOUNTING_TAB, VALUATIONS_FIELD, column);
    },
    onNumberChange(row, column, value) {
      row[column] = value;
      this.onColumnChange(ACCOUNTING_TAB, VALUATIONS_FIELD, column, row);
    },
    // Hook kerangka bersama, dipanggil tiap item dipilih/berganti: tabel kosong dibuatkan satu baris kosong (bukan mode
    // lihat-saja; baris yang tetap kosong tidak ikut payload Save), grup split dikenali ulang, label opsi dimuat.
    onItemBound() {
      this.ensureTableArrays(ACCOUNTING_TAB);
      const rows = this.mcrItem[VALUATIONS_FIELD];
      // Baris kosong cuma dibuat kalau baris boleh ditambah (`row_actions.add`, mis. INALUM: harus ada plant selain Plant NV).
      if (!this.disabled && Array.isArray(rows) && !rows.length && this.isTableVisible(ACCOUNTING_TAB, VALUATIONS_FIELD)
        && this.canAddRow(ACCOUNTING_TAB, VALUATIONS_FIELD)) {
        this.addRow(ACCOUNTING_TAB, VALUATIONS_FIELD);
      }
      this.rebuildSplitGroups(ACCOUNTING_TAB);
      // Default kolom dari config (mis. Valuation Category ANTAM "Z" untuk Material Type ZM02-ZM06): sel kosong diisi saat item
      // dibuka (`fill_empty`), dihitung ulang tiap field form yang dirujuk `when`-nya berubah. Mode lihat-saja dilewati di kerangka.
      this.fillEmptyColumnDefaults(ACCOUNTING_TAB);
      // Baris pendamping dikenali ulang / dilengkapi (mis. ANTAM: baris Valuation Type "" per Valuation Area + Category).
      this.syncAllCompanionRows(ACCOUNTING_TAB);
      this.watchExternalRowTriggers(ACCOUNTING_TAB);
      this.preloadTabLabels(ACCOUNTING_TAB);
    }
  }
};
</script>
